import type {
  SocialPostEditableDraft,
  SocialPostSceneBlockV1,
  SocialPostScenePlanV1,
} from "@runcheetah/shared";

type LogoAssetInput = {
  objectKey: string;
  isPrimary?: boolean | null;
};

export function normalizeSocialPostDraftLogoAssetKeys(input: {
  draft: SocialPostEditableDraft | null;
  logoAssets: readonly LogoAssetInput[];
}): SocialPostEditableDraft | null {
  if (!input.draft) {
    return null;
  }

  const primaryLogo =
    input.logoAssets.find((logo) => logo.isPrimary) ?? input.logoAssets[0] ?? null;
  const validLogoObjectKeys = new Set(input.logoAssets.map((logo) => logo.objectKey));

  return {
    ...input.draft,
    plan: normalizeSocialPostPlanLogoAssetKeys({
      plan: input.draft.plan,
      validLogoObjectKeys,
      fallbackLogoObjectKey: primaryLogo?.objectKey ?? null,
    }),
    carouselSlides: input.draft.carouselSlides.map((slide) => ({
      ...slide,
      plan: normalizeSocialPostPlanLogoAssetKeys({
        plan: slide.plan,
        validLogoObjectKeys,
        fallbackLogoObjectKey: primaryLogo?.objectKey ?? null,
      }),
    })),
    variantDrafts: input.draft.variantDrafts.map((variant) => ({
      ...variant,
      plan: normalizeSocialPostPlanLogoAssetKeys({
        plan: variant.plan,
        validLogoObjectKeys,
        fallbackLogoObjectKey: primaryLogo?.objectKey ?? null,
      }),
      carouselSlides: variant.carouselSlides.map((slide) => ({
        ...slide,
        plan: normalizeSocialPostPlanLogoAssetKeys({
          plan: slide.plan,
          validLogoObjectKeys,
          fallbackLogoObjectKey: primaryLogo?.objectKey ?? null,
        }),
      })),
    })),
  };
}

function normalizeSocialPostPlanLogoAssetKeys(input: {
  plan: SocialPostScenePlanV1;
  validLogoObjectKeys: Set<string>;
  fallbackLogoObjectKey: string | null;
}): SocialPostScenePlanV1 {
  return {
    ...input.plan,
    scene: {
      ...input.plan.scene,
      blocks: input.plan.scene.blocks.map((block) =>
        normalizeSocialPostBlockLogoAssetKey({
          block,
          validLogoObjectKeys: input.validLogoObjectKeys,
          fallbackLogoObjectKey: input.fallbackLogoObjectKey,
        }),
      ),
    },
  };
}

function normalizeSocialPostBlockLogoAssetKey(input: {
  block: SocialPostSceneBlockV1;
  validLogoObjectKeys: Set<string>;
  fallbackLogoObjectKey: string | null;
}): SocialPostSceneBlockV1 {
  if (input.block.type !== "logo") {
    return input.block;
  }

  const currentObjectKey = input.block.assetObjectKey ?? null;
  if (currentObjectKey && input.validLogoObjectKeys.has(currentObjectKey)) {
    return input.block;
  }

  if (input.fallbackLogoObjectKey) {
    return { ...input.block, assetObjectKey: input.fallbackLogoObjectKey };
  }

  const { assetObjectKey: _assetObjectKey, ...blockWithoutLogoAsset } = input.block;
  return blockWithoutLogoAsset;
}
